import { defineStore } from 'pinia';
import Post from '~/models/Post';
import type PostDetail from '~/models/PostDetail';
import { DateTime, Interval } from 'luxon';
import { usePinia } from '#imports';
import { handleErrorAndNotificationHandler } from '~/util/errorNotificationHandler';
import type { AxiosHeaders } from 'axios';
import { AxiosError } from 'axios';
import type { Pinia } from './shared/sharedTypes';
import type {
  MemberPost,
  Paginator,
  ClassificationNodes,
  ClassificationItems,
  HttpHeaders,
  ClassSchema,
  SharingScreen,
  ComplianceSignature,
  ComplianceRuleSet,
  AcknowledgeData,
  Facet,
  AvailableItems,
  UserTaxonomy,
  Collections,
  Response,
  AvailableItemsResponse,
  Permissions,
} from '~/stores/Member Posts/memberPostsTypes';

const findCategoryById = (
  array: ClassificationItems[],
  categoryId: number
): ClassificationItems | null => {
  for (const item of array) {
    if (item.categoryId === categoryId) {
      return item;
    } else if (item.hasChildren && item.items) {
      const foundItem = findCategoryById(item.items, categoryId);
      if (foundItem) {
        return foundItem;
      }
    }
  }
  return null;
};

function filterPosts(
  arr: Post[],
  interval: Interval,
  excludeIntervals: Interval[] = []
) {
  return arr === undefined
    ? []
    : arr
        .filter((item) => item.postDetail.versionLabel !== 'DRAFT')
        .filter((i) => {
          const postTime = DateTime.fromMillis(
            i.postDetail.lastPublishDateTimeMillis
          );
          return (
            interval.contains(postTime) &&
            excludeIntervals.every((ex) => !ex.contains(postTime))
          );
        })
        .sort(
          (a, b) =>
            b.postDetail.lastPublishDateTimeMillis -
            a.postDetail.lastPublishDateTimeMillis
        );
}

interface GeneratedContent {
  title: string;
  content: string;
}

const postObject: Post = {
  newsContent: '',
  checksum: '',
  courseName: '',
  courseVideoPosterUrl: '',
  documentPreviewData: {},
  downloadUrl: '',
  eventEndDateTimeMillis: 0,
  eventLocation: '',
  eventStartDateTimeMillis: 0,
  fileSizeInBytes: 0,
  mimeType: '',
  postDetail: {} as PostDetail,
  repositoryId: 0,
  srcUrl: '',
  title: '',
};

type RequiredActionType = 'NO_ACTION_REQUIRED' | 'ACKNOWLEDGE' | 'SIGNATURE';
type ComplianceMembershipParticipationTypes = 'ANY_MEMBER' | 'ALL_MEMBERS';
interface ClassPaginatorType {
  [key: string]: unknown;
}

interface PaginatorType {
  [key: string]: Partial<Paginator>;
}

interface OrderDocument {
  fileName: string;
  data: string;
  fileSizeInBytes: number;
}

interface ServiceItemDocumentsResponse {
  orderDocuments: OrderDocument[];
  orderDocumentCount: number;
}
interface MemberPostsState {
  posts: Record<string, Post>;
  paginator: Paginator[];
  classPaginator: ClassPaginatorType;
  activeClassification: UserTaxonomy | null;
  classificationNodes: ClassificationNodes | null;
  classCategory: Partial<ClassificationItems>;
  classSubcategory: Partial<ClassificationItems>;
  classificationPosts: Record<string, Post>;
  syncing: boolean;
  reqActionsPaginator: PaginatorType;
  unreadPaginator: PaginatorType;
  pageName: string;
  currentChannelId: string | number;
  complianceActions: RequiredActionType[] | unknown;
  complianceMembershipTypes: ComplianceMembershipParticipationTypes[] | unknown;
  postCreated: boolean;
  versionLabel: string;
  newsContent: GeneratedContent | null;
  metaData: Permissions;
  latestPostDetail: Post | null;
  newsTitle: string;
  createNewPostUrl: HttpHeaders[];
  metaDataRoute: string;
  metaDataTaxonomyId: string;
  postsShown: Post[];
  classificationsPostsShown: Post[];
  classSchema: Partial<ClassSchema>;
  sharingData: Partial<SharingScreen>;
  uploadedFile: File[];
  complianceSignature: ComplianceSignature | null;
  signaturePin: string;
  storedComplianceRules: ComplianceRuleSet | null;
  acknowledgeData: AcknowledgeData | null;
  reqActions: Post[];
  unreadPosts: Post[];
  availableFacets: Facet[];
  filterLoading: boolean;
  availableItems: AvailableItems[];
  selectedChannelId: string | number;
  totalFilteredCount: number;
  markUnread: string;
  postDetailError: number | null;
  serviceItemDetail: MemberPost[];
  orderDocuments: OrderDocument[];
  orderDocumentCount: number;
  documentsByServiceId: Record<number, OrderDocument[]>;
}

interface MemberPostsAPI {
  postDetailVersion(postId: string, version: string): string;
  updateNewsContent(postId: string): string;
  updatedPostsTitle(postId: string): string;
  updatedPostsPermissions(postId: string): string;
  updatedPostsClassifications(postId: string): string;
  detailsLatestVersion(postId: string): string;
  fetchSinglePostSummary(
    postId: string,
    queryParams: Record<string, unknown>
  ): string;
  approveRejectPostDraft(
    postId: string,
    queryParams: Record<string, unknown>
  ): string;
  deletePostAsset(postId: string): string;
  updateDocumentData(
    postId: string,
    queryParams: Record<string, unknown>
  ): string;
  postSignatureComplianceAction(postId: string): string;
  complianceRuleset(postId: string): string;
  postAcknowledgeComplianceAction(postId: string): string;
  verifyComplianceRecordSignature(postId: string, recordId: number): string;
  unreadMarker(postId: string): string;
  fetchServiceItem(serviceItemId: number): string;
  fetchServiceItemDocuments(serviceItemId: number): string;
}
interface UserPostsAPI {
  memberPosts(queryParams: Record<string, unknown>): string;
  channelMemberPosts(queryParams: Record<string, unknown>): string;
  generateUserSignature(): string;
  subscribeUserToPost(postId: string): string;
  facetSelections(queryParams: Record<string, unknown>): string;
}

interface ChannelsPostsApi {
  classificationNodes(
    channelId: number | string,
    taxonomyId: number | undefined,
    queryParams: Record<string, unknown>
  ): string;
  classificationNodes(
    channelId: number | string,
    taxonomyId: number | undefined,
    categoryId: number,
    queryParams: Record<string, unknown>
  ): string;
  classificationSchema(
    channelId: number | string,
    taxonomyId: number | undefined,
    queryParams: Record<string, unknown>
  ): string;
  getSharingScreen(channelId: number | string): string;
  createNewsPost(channelId: number | string): string;
}

interface AppPostsApi {
  complianceRequiredActionTypes: string;
  complianceMembershipParticipationTypes: string;
}
interface API {
  posts: MemberPostsAPI;
  user: UserPostsAPI;
  channels: ChannelsPostsApi;
  app: AppPostsApi;
}

export const useMemberPostStore = defineStore('memberPosts', {
  state: (): MemberPostsState => ({
    posts: {},
    paginator: [],
    classPaginator: {},
    activeClassification: null,
    classificationNodes: null,
    classCategory: {},
    classSubcategory: {},
    classificationPosts: {},
    syncing: false,
    reqActionsPaginator: {},
    unreadPaginator: {},
    pageName: '',
    currentChannelId: 0,
    complianceActions: [],
    complianceMembershipTypes: [],
    postCreated: false,
    versionLabel: '',
    newsContent: null,
    metaData: {} as Permissions,
    latestPostDetail: null,
    newsTitle: '',
    createNewPostUrl: [],
    metaDataRoute: '',
    metaDataTaxonomyId: '',
    postsShown: [],
    classificationsPostsShown: [],
    classSchema: {},
    sharingData: {},
    uploadedFile: [] as File[],
    complianceSignature: null,
    signaturePin: '',
    storedComplianceRules: null,
    acknowledgeData: null,
    reqActions: [],
    unreadPosts: [],
    availableFacets: [],
    filterLoading: false,
    availableItems: [],
    selectedChannelId: '',
    totalFilteredCount: 0,
    markUnread: '',
    postDetailError: null,
    serviceItemDetail: [],
    orderDocuments: [],
    orderDocumentCount: 0,
    documentsByServiceId: {},
  }),
  actions: {
    overridePaginator(paginator, channelId = 'allChannels') {
      this.paginator[channelId] = paginator;
    },
    mutateCreateNewUrl(postId) {
      const updatedArr = this.createNewPostUrl.filter((item) => {
        const extractedPostId = item.location.split('/').pop();
        return extractedPostId !== postId;
      });
      this.createNewPostUrl = updatedArr;
    },
    async getAvailableFacets({ query, cleanup }) {
      //this should be a part of search store
      try {
        const pinia = usePinia() as Pinia<API>;
        this.filterLoading = true;
        const { facets, items, totalCount } = (await pinia.$axios.$post(
          pinia.$API.user.facetSelections(query)
        )) as AvailableItemsResponse;
        this.availableFacets = facets;
        if (!cleanup) {
          this.availableItems = items;
          this.totalFilteredCount = totalCount;
        } else {
          this.availableItems = [];
        }
        this.filterLoading = false;
      } catch (e) {
        handleErrorAndNotificationHandler('fetching available facets:', e);
        this.filterLoading = false;
      }
    },
    resetFilters() {
      this.availableFacets = [];
      this.availableItems = [];
      this.totalFilteredCount = 0;
    },
    removePostsFromCollection(channelId: string) {
      const channelIdNum = parseInt(channelId, 10);

      Object.entries(this.posts).forEach(([key, post]) => {
        if (post.postDetail.channelId === channelIdNum) {
          this.posts = Object.fromEntries(
            Object.entries(this.posts).filter(([k]) => k !== key)
          );
        }
      });
    },
    setSelectedChannelId(selectedChannelId: number | string) {
      this.selectedChannelId = selectedChannelId;
    },

    async getVersionPostDetail(payload: {
      postId: string;
      version: string;
    }): Promise<void> {
      const { postId, version } = payload;
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp: Post = await pinia.$axios.$get(
          pinia.$API.posts.postDetailVersion(postId, version)
        );
        const key = `${postId}>${version}`;
        const existingKey = Object.keys(this.posts).find(
          (postKey) =>
            postKey.startsWith(`${postId}>`) &&
            this.posts[postKey]?.postDetail?.versionLabel !== 'DRAFT'
        );
        if (existingKey) {
          const { [existingKey]: _, ...rest } = this.posts;
          this.posts = rest;
        }
        this.posts[key] = new Post(resp);
        this.postDetailError = null;
      } catch (e: unknown) {
        handleErrorAndNotificationHandler(
          'Error fetching version post detail:',
          e
        );

        if (e instanceof AxiosError && typeof e.response?.status === 'number') {
          this.postDetailError = e.response.status;
        } else {
          this.postDetailError = null;
        }
      }
    },
    async memberPostsSync(payload: {
      channelId: number | string;
      pageSize: number;
      sort: string;
      filter: string;
      facets: Facet[];
      search: string;
      unreadPosts?: boolean;
      complianceActionPosts?: boolean;
      page: number;
    }): Promise<void> {
      const {
        channelId,
        pageSize,
        sort,
        filter,
        facets,
        search,
        unreadPosts,
        complianceActionPosts,
        page,
      } = payload;
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;
      const channelKey = channelId || 'allChannels';

      try {
        const resp: Response = await pinia.$axios.$post(
          pinia.$API.user.memberPosts(
            new Query({
              channelId,
              pageSize,
              page,
              sort,
              filter,
              facets,
              search,
              unreadPosts,
              complianceActionPosts,
            }).getQueryParameters()
          ),
          []
        );

        const { items, ...paginator } = resp;

        items.forEach((item: PostDetail) => {
          const key = `${item.postId}>${item.version}`;
          if (this.posts[key]) {
            return;
          }
          postObject.postDetail = item;
          this.posts = {
            ...this.posts,
            [key]: new Post(postObject),
          };
        });

        if (complianceActionPosts) {
          this.reqActionsPaginator[channelKey] = paginator;
        } else if (unreadPosts) {
          this.unreadPaginator[channelKey] = paginator;
        } else {
          this.paginator[channelKey] = paginator;
        }
        const data = Object.values(this.posts);

        if (this.pageName.includes('unread-posts')) {
          this.unreadPosts = data.filter(
            (i) => !i.postDetail?.hasViewedLastModification
          );
        } else if (this.pageName.includes('required-actions')) {
          this.reqActions = data.filter(
            (i) => i.postDetail?.metadata.compliance?.requiredActions
          ); //this is corrected part of the code since required actions never existed on a postDetail level 10.07.2025.
        } else {
          this.unreadPosts = [];
          this.reqActions = [];
        }
      } catch (error) {
        handleErrorAndNotificationHandler('Error in member posts sync:', error);
      }
    },

    removeEvent(uuid: string): void {
      //this action is used, but unclear when it comes to purpose, i.id do not exist in post object therefore to excape ts error i.postDetail.postId is beeing used .
      this.unreadPosts = this.unreadPosts.filter(
        (i) => i.postDetail?.postId !== uuid
      );
    },
    setPageName(pageName: string) {
      this.pageName = pageName;
    },
    setCurrentChannelId(payload: string | number): void {
      this.currentChannelId = payload;
    },
    resetPostCollection() {
      this.posts = {};
    },
    async piniaPostSummariesFetch(payload: {
      pageSize: number;
      channelId: number | string;
      includeDraft: boolean;
      page: number;
      sort: string;
      filter: string;
      facets: Facet[];
    }): Promise<void> {
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;
      const API = pinia.$API;
      const axios = pinia.$axios;
      const { pageSize, channelId, includeDraft, page, sort, filter, facets } =
        payload;
      const query = new Query({
        channelId,
        pageSize,
        page,
        sort,
        filter,
        facets,
        includeDraft,
      }).getQueryParameters();

      try {
        const response: Response = await axios.$post(
          API.user.memberPosts(query),
          []
        );
        const { items, ...paginator } = response;
        items.forEach((item) => {
          const key = `${item.postId}>${item.version}`;
          if (this.posts[key]) {
            return;
          }
          postObject.postDetail = item;
          this.posts = {
            ...this.posts,
            [key]: new Post(postObject),
          };
        });
        const values = Object.values(this.posts);
        if (Object.values(this.posts).length === 0) {
          this.postsShown = [];
        }
        if (this.pageName === 'channel-channelId-timeline') {
          const channelId =
            typeof this.currentChannelId === 'string'
              ? parseInt(this.currentChannelId)
              : this.currentChannelId;
          const postsByChannel = values.filter(
            (item) => item.postDetail?.channelId === channelId
          );
          this.postsShown = postsByChannel;
        }
        const paginatorKey = this.currentChannelId
          ? String(this.currentChannelId)
          : 'allChannels';
        this.paginator = {
          ...this.paginator,
          [paginatorKey]: paginator,
        };
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in pinia post summaries fetch:',
          error
        );
      }
    },
    async activeChannelPostsNextPage(payload: {
      pageSize: number;
      channelId: string | number;
      sort: string;
      filter: string;
      facets: Facet[];
    }): Promise<void> {
      const { pageSize, channelId, sort, filter, facets } = payload;
      try {
        const key =
          this.currentChannelId != null
            ? String(this.currentChannelId)
            : 'allChannels';

        const page = this.paginator[key].page + 1;
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;

        const query = new Query({
          channelId,
          pageSize,
          page,
          sort,
          filter,
          facets,
          includeDraft: false,
        }).getQueryParameters();
        const resp: Response = await pinia.$axios.$post(
          pinia.$API.user.memberPosts(query),
          []
        );
        const { items, ...paginator } = resp;
        items.forEach((item) => {
          const key2 = `${item.postId}>${item.version}`;
          if (!this.posts[key2]) {
            postObject.postDetail = item;
            this.posts = {
              ...this.posts,
              [key2]: new Post(postObject),
            };
          }
        });
        if (this.pageName === 'channel-channelId-timeline') {
          if (Object.values(this.posts).length === 0) {
            this.postsShown = [];
          } else {
            const values = Object.values(this.posts);
            const payloadChannelId =
              typeof channelId === 'string' ? parseInt(channelId) : channelId;
            const postsByChannel = values.filter(
              (item) => item.postDetail?.channelId === payloadChannelId
            );
            this.postsShown = postsByChannel;
          }
        }
        this.paginator[key] = paginator;
        if (resp.lastPage) {
          this.paginator[key].lastPage = true;
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in active channel posts next page:',
          error
        );
      }
    },

    activeChannelPostsSync(syncingStatus: boolean) {
      this.syncing = syncingStatus;
    },
    setActiveClassification(activeClassification: UserTaxonomy) {
      this.activeClassification = activeClassification;
    },

    clearPostsShown() {
      this.postsShown = [];
    },
    async classNodes(item: UserTaxonomy): Promise<void> {
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;

        if (!item?.categoryId || item?.categoryId === 0) {
          const channelId = this.currentChannelId;
          const taxonomyId = this.activeClassification?.taxonomyId;
          const resp: ClassificationNodes = await pinia.$axios.$get(
            pinia.$API.channels.classificationNodes(
              channelId,
              taxonomyId,
              new Query({ pageSize: -1, depth: 0 }).getQueryParameters()
            )
          );
          const data = resp.items.map((item2) => ({
            ...item2,
            header: 'header',
            title: item2.name,
            items: item2.hasChildren ? [] : undefined,
            id: item2.categoryId,
          }));
          resp.items = data;
          this.classificationNodes = resp;
        } else if (item?.categoryId) {
          const resp: ClassificationNodes = await pinia.$axios.$get(
            pinia.$API.channels.classificationNodes(
              this.currentChannelId,
              this.activeClassification?.taxonomyId,
              item.categoryId,
              new Query({ pageSize: -1 }).getQueryParameters()
            )
          );

          const updateItems = (
            items: ClassificationItems[] | undefined,
            categoryId: number,
            newData: ClassificationItems[]
          ): boolean => {
            if (!items) return false;

            for (const item of items) {
              if (item.categoryId === categoryId) {
                item.items = newData;
                (item as ClassificationItems).displayClassSubcategory = true;
                return true;
              }
              if (item.hasChildren && item.items?.length) {
                const updated = updateItems(item.items, categoryId, newData);
                if (updated) return true;
              }
            }
            return false;
          };
          const updatedItems = JSON.parse(
            JSON.stringify(this.classificationNodes?.items)
          );
          const data1 = resp.items.map((item1) => ({
            ...item1,
            title: item1.name,
            header: 'header',
            items: !item1.hasChildren ? undefined : [],
            id: item1.categoryId,
          }));
          updateItems(updatedItems, item.categoryId, data1);
          if (this.classificationNodes) {
            this.classificationNodes.items = updatedItems;
          }
        }
      } catch (error) {
        handleErrorAndNotificationHandler('Error in class nodes:', error);
      }
    },

    async setClassificationPosts(payload: {
      channelId: number | string;
      classification: string;
      pageSize: number;
    }) {
      const { channelId, classification, pageSize } = payload;
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        const resp: Response = await pinia.$axios.$post(
          pinia.$API.user.channelMemberPosts(
            new Query({
              channelId,
              pageSize,
              filter: `taxonomyCategoryIds/any(t:t eq ${classification})`,
            }).getQueryParameters()
          ),
          []
        );
        const { items, ...classPaginator } = resp;

        let key: string | number | null = null;
        if (this.classCategory?.categoryId) {
          key = this.classCategory.categoryId;
        } else if (this.classSubcategory.categoryId) {
          key = this.classSubcategory.categoryId;
        } else {
          key = null;
        }

        if (key !== null) {
          this.classPaginator[key] = classPaginator;
        }
        this.classificationPosts = Object.fromEntries(
          items.map((postDetail) => {
            const post = new Post({ postDetail }); // Assuming you construct a Post with a PostDetail
            const key = postDetail.postId; // You must ensure postDetail has a postId
            return [key, post];
          })
        );
        resp.items.forEach((item) => {
          const key2 = `${item.postId}>${item.version}`;
          if (this.posts[key2]) {
            return;
          }
          postObject.postDetail = item;
          this.posts = {
            ...this.posts,
            [key2]: new Post(postObject),
          };
        });

        const values = Object.values(this.posts);
        const postsByChannel = values.filter(
          (item) => item.postDetail?.channelId === channelId
        );
        const postsByClassification = postsByChannel.filter((item) =>
          resp.items.some((i) => i.postId === item.postDetail?.postId)
        );
        this.classificationsPostsShown = postsByClassification;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in setting classification posts:',
          error
        );
      }
    },

    async setClassificationPostsNextPage(payload: {
      pageSize: number;
      channelId: string | number;
      classification: Partial<ClassificationItems>;
    }): Promise<void> {
      const { channelId, pageSize, classification } = payload;

      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;

        let key =
          this.currentChannelId !== null
            ? String(this.currentChannelId)
            : 'allChannels';
        const page = this.paginator[key].page + 1;

        const query = new Query({
          channelId,
          pageSize,
          page,
          classification,
          filter: `taxonomyCategoryIds/any(t:t eq ${classification.categoryId})`,
        }).getQueryParameters();

        const resp: Response = await pinia.$axios.$post(
          pinia.$API.user.channelMemberPosts(query),
          []
        );

        const { items, ...classPaginator } = resp;

        if (this.classCategory.categoryId) {
          key = String(this.classCategory.categoryId);
        } else if (this.classSubcategory.categoryId) {
          key = String(this.classSubcategory.categoryId);
        }

        this.classPaginator[key] = classPaginator;
        this.classificationPosts = Object.fromEntries(
          items.map((postDetail) => {
            const post = new Post({ postDetail });
            const key = postDetail.postId;
            return [key, post];
          })
        );

        items.forEach((item) => {
          const key2 = `${item.postId}>${item.version}`;
          if (this.posts[key2]) {
            return;
          }
          postObject.postDetail = item;
          this.posts = {
            ...this.posts,
            [key2]: new Post(postObject),
          };
        });

        const values = Object.values(this.posts);
        const postsByChannel = values.filter(
          (item) => item.postDetail?.channelId === channelId
        );
        const postsByClassification = postsByChannel.filter((item) =>
          resp.items.some((i) => i.postId === item.postDetail?.postId)
        );

        this.classificationsPostsShown = postsByClassification;

        if (resp.lastPage) {
          this.paginator[key].lastPage = true;
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in setting classification posts next page:',
          error
        );
      }
    },
    setCategory(categoryId: number) {
      if (this.classificationNodes) {
        const category = findCategoryById(
          this.classificationNodes.items,
          categoryId
        );
        if (category) {
          this.classCategory = category;
        }
      }
    },

    async getComplianceActions() {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp = await pinia.$axios.$get(
          pinia.$API.app.complianceRequiredActionTypes
        );
        this.complianceActions = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching compliance actions:',
          error
        );
      }
    },
    async getComplianceMembershipTypes() {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp = await pinia.$axios.$get(
          pinia.$API.app.complianceMembershipParticipationTypes
        );
        this.complianceMembershipTypes = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching compliance membership types:',
          error
        );
      }
    },
    postCreationInfo(postCreated: boolean) {
      this.postCreated = postCreated;
    },
    postVersionLabel(versionLabel: string) {
      this.versionLabel = versionLabel;
    },
    newsContentFn(newsContent: GeneratedContent) {
      this.newsContent = newsContent;
    },
    async updateNewsContent(postId: string) {
      try {
        const pinia = usePinia() as Pinia<API>;
        return await pinia.$axios.patch(
          pinia.$API.posts.updateNewsContent(postId),
          {
            title: this.newsContent?.title,
            content: this.newsContent?.content,
          }
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error updating news content:',
          error
        );
        return null; // Return a value in case of an error
      }
    },
    postMetaData(metaData: Permissions) {
      this.metaData = metaData;
    },
    async updatePostMetaData(postId: string) {
      try {
        const pinia = usePinia() as Pinia<API>;

        if (this.metaData.title) {
          const endpoint =
            this.metaDataRoute === 'post-postId-version-detail'
              ? pinia.$API.posts.updatedPostsTitle(postId)
              : pinia.$API.posts.updatedPostsTitle(postId);

          const method =
            this.metaDataRoute === 'post-postId-version-detail'
              ? 'patch'
              : 'put';

          const response = await pinia.$axios[method](endpoint, {
            title: this.metaData.title,
          });
          return response;
        }

        if (this.metaData?.viewPost) {
          const response = await pinia.$axios.$put(
            pinia.$API.posts.updatedPostsPermissions(postId),
            {
              viewPost: {
                channelWide: this.metaData.viewPost.channelWide,
                roles: this.metaData.viewPost.roles,
              },
              commentOnPost: {
                channelWide: this.metaData.commentOnPost.channelWide,
                roles: this.metaData.commentOnPost.roles,
              },
              updatePost: {
                channelWide: this.metaData.updatePost.channelWide,
                roles: this.metaData.updatePost.roles,
              },
              deletePost: {
                channelWide: this.metaData.deletePost.channelWide,
                roles: this.metaData.deletePost.roles,
              },
            }
          );
          return response;
        }

        if (!this.metaData.title && !this.metaData?.viewPost) {
          const data = this.metaData;
          const response = await pinia.$axios.$put(
            pinia.$API.posts.updatedPostsClassifications(postId),
            data
          );
          return response;
        }

        return null;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error updating post metadata:',
          error
        );
        return null; // just to pass Deep Source code analysis
      }
    },
    resetLatestPostDetail() {
      this.latestPostDetail = null;
    },
    async getPostDetailCreation(postId: string) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp: Post = await pinia.$axios.$get(
          pinia.$API.posts.detailsLatestVersion(postId)
        );
        this.latestPostDetail = resp;

        if (resp !== undefined) {
          const key = `${resp.postDetail?.postId}>${resp.postDetail?.version}`;
          this.posts = { ...this.posts, [key]: resp };
        }
      } catch (error) {
        const keyToRemove = postId.toString();

        const updatedPosts = Object.fromEntries(
          Object.entries(this.posts).filter(
            ([key]) => !key.includes(keyToRemove)
          )
        );

        this.posts = updatedPosts;
        this.postsShown = this.postsShown.filter(
          (post) => post.postDetail?.postId !== postId
        );
        handleErrorAndNotificationHandler(
          'Error fetching post detail creation:',
          error
        );
      }
    },

    async getSinglePostSummary(payload: { postId: string }): Promise<void> {
      const { postId } = payload;
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        const resp: PostDetail = await pinia.$axios.$get(
          pinia.$API.posts.fetchSinglePostSummary(
            postId,
            new Query({ showOnlySummary: true }).getQueryParameters()
          )
        );
        const key = `${resp.postId}>${resp.version}`;
        postObject.postDetail = resp;
        const newPost = new Post(postObject);
        if (!this.posts[key]) {
          this.postsShown.push(newPost);
        }
        if (this.posts[key]) {
          this.posts[key].postDetail = resp;
        }
        this.posts = { ...this.posts, [key]: newPost };
        if (this.posts[key]?.postDetail?.versionLabel === 'DRAFT') {
          const { [key]: _, ...rest } = this.posts;
          this.posts = rest;

          this.postsShown = this.postsShown.filter(
            (item) => item.postDetail?.postId !== resp.postId
          );
        }
        const channelId =
          typeof this.currentChannelId === 'string'
            ? parseInt(this.currentChannelId)
            : this.currentChannelId;
        this.postsShown = this.postsShown.filter(
          (p) => p.postDetail?.channelId === channelId
        );
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          const status = error.response?.status;

          if (typeof status === 'number' && status >= 400 && status < 500) {
            const keyToRemove = postId.toString();

            const updatedPosts = Object.fromEntries(
              Object.entries(this.posts).filter(
                ([key]) => !key.includes(keyToRemove)
              )
            );

            this.posts = updatedPosts;

            this.postsShown = this.postsShown.filter(
              (post) => post.postDetail?.postId !== postId
            );
          }
        }

        handleErrorAndNotificationHandler(
          'Error fetching single post summary:',
          error
        );
      }
    },
    newsTitleFn(newsTitle: string) {
      this.newsTitle = newsTitle;
    },
    async statusPostDraft(payload: {
      postId: string;
      statusName: string;
      versionDescription: string;
    }): Promise<void> {
      const { postId, statusName, versionDescription } = payload;
      const pinia = usePinia() as Pinia<API>;
      const Query = pinia.$Query;
      if (statusName !== 'approve_draft') {
        try {
          await pinia.$axios.$put(
            pinia.$API.posts.approveRejectPostDraft(
              postId,
              new Query({ transitionName: statusName }).getQueryParameters()
            ),
            {
              versionDescription: '',
            }
          );
        } catch (error) {
          handleErrorAndNotificationHandler(
            'error in status post draft',
            error
          );
        }
      } else {
        try {
          await pinia.$axios.$put(
            pinia.$API.posts.approveRejectPostDraft(
              postId,
              new Query({ transitionName: statusName }).getQueryParameters()
            ),
            {
              versionDescription,
            }
          );
        } catch (error) {
          handleErrorAndNotificationHandler(
            'Error in status post draft:',
            error
          );
        }
      }
    },
    createDocument(headers: HttpHeaders) {
      this.createNewPostUrl.push(headers);
    },
    changeVersionLabelOfPost(payload: { postId: string; version: string }) {
      const key = `${payload.postId}>${payload.version}`;
      if (this.posts[key].postDetail) {
        this.posts[key].postDetail.versionLabel = 'SUPERCEDED';
      }
    },
    updateVersionPostDetail(payload: { post: Post; title: string }) {
      const payloadPostId = payload.post?.postDetail?.postId;

      if (payloadPostId) {
        this.postsShown = this.postsShown.filter(
          (post) => post.postDetail?.postId !== payloadPostId
        );
      }

      const postId = payload.post?.postDetail?.postId;
      const version = payload.post?.postDetail?.version;

      if (postId && version) {
        const key = `${postId}>${version}`;

        if (payload.title && this.posts[key]?.postDetail) {
          this.posts[key].postDetail.title = payload.title;
        }

        if (this.posts[key]) {
          this.postsShown.push(this.posts[key]);
        }
      }
    },
    resetFields() {
      this.metaDataTaxonomyId = '';
      this.metaData = {} as Permissions;
      this.newsTitle = '';
      this.createNewPostUrl = [];
      this.latestPostDetail = null;
      this.storedComplianceRules = null;
      this.postDetailError = null;
      this.newsContent = null;
    },
    piniaMetaDataRoute(route: string) {
      this.metaDataRoute = route;
    },
    setMetaDataTaxonomyId(metaDataTaxonomyId: string) {
      this.metaDataTaxonomyId = metaDataTaxonomyId;
    },
    async deletePost(postId: string) {
      try {
        const pinia = usePinia() as Pinia<API>;
        await pinia.$axios.$delete(pinia.$API.posts.deletePostAsset(postId));
        const keyToRemove = postId.toString();

        const updatedPosts = Object.keys(this.posts)
          .filter((key) => !key.includes(keyToRemove))
          .reduce((obj, key) => {
            obj[key] = this.posts[key];
            return obj;
          }, {});

        this.posts = updatedPosts;
        this.postsShown = this.postsShown.filter(
          (post) => post.postDetail?.postId !== postId
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error deleting post:', error);
      }
    },
    removePostFromCollection(payload: string) {
      const keyToRemove = payload;
      const updatedPosts = Object.keys(this.posts)
        .filter((key) => !key.includes(keyToRemove))
        .reduce((obj, key) => {
          obj[key] = this.posts[key];
          return obj;
        }, {});

      this.posts = updatedPosts;
      this.postsShown = this.postsShown.filter(
        (post) => post.postDetail?.postId !== payload
      );
    },
    async classificationSchema(payload: {
      channelId: number | string;
      taxonomyId: number;
      includeAdditionalProperties: boolean;
    }) {
      let { channelId } = payload;
      const { taxonomyId, includeAdditionalProperties } = payload;
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.classificationSchema(
            channelId,
            taxonomyId,
            new Query({
              includeAdditionalProperties,
            }).getQueryParameters()
          )
        );
        const key = (channelId += `>${taxonomyId}`);
        this.classSchema = {
          ...this.classSchema,
          [key]: resp,
        };
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in classification schema:',
          error
        );
      }
    },
    async sharingScreenData(channelId: string | number) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.getSharingScreen(channelId)
        );
        const key = channelId;
        this.sharingData = { ...this.sharingData, [key]: resp };
      } catch (error) {
        handleErrorAndNotificationHandler('Error sharing screen data:', error);
      }
    },
    async updatePostContent(payload: {
      postId: string;
      description: string;
    }): Promise<void> {
      const { postId, description } = payload;
      try {
        const formData = new FormData();
        const imageFile = this.uploadedFile[0];
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        formData.append('file', imageFile as File);
        await pinia.$axios.$patch(
          pinia.$API.posts.updateDocumentData(
            postId,
            new Query({
              description,
            }).getQueryParameters()
          ),
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error updating post content:',
          error
        );
      }
    },
    uploadedFiles(uploadedFile: File[]) {
      this.uploadedFile = uploadedFile;
    },
    async createPostNews(channelId: string | number) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const { headers }: AxiosHeaders = await pinia.$axios.post(
          pinia.$API.channels.createNewsPost(channelId),
          {
            title: this.newsContent?.title,
            content: this.newsContent?.content,
          }
        );
        /* if (resp) {
          commit(MEMBER_POST_STORE_CONSTANTS.ERROR_MESSAGE, resp);
        } */
        this.createNewPostUrl.push(headers);
      } catch (error) {
        handleErrorAndNotificationHandler('Error creating post news:', error);
      }
    },
    storeComplianceSignatureData(payload: ComplianceSignature) {
      this.complianceSignature = payload;
    },
    async postComplianceSignature(postId: string) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp: ComplianceSignature = await pinia.$axios.$post(
          pinia.$API.posts.postSignatureComplianceAction(postId),
          {
            teamId: this.complianceSignature?.teamId,
            ruleId: this.complianceSignature?.ruleId,
            signaturePIN: this.complianceSignature?.signaturePIN,
            approvalType: this.complianceSignature?.approvalType,
          }
        );
        this.complianceSignature = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in post compliance signature:',
          error
        );
      }
    },
    async generateSignaturePin() {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp: string = await pinia.$axios.$post(
          pinia.$API.user.generateUserSignature()
        );
        this.signaturePin = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error generating signature pin:',
          error
        );
      }
    },
    complianceRulesetStore(payload: ComplianceRuleSet) {
      this.storedComplianceRules = payload;
    },
    async complianceRuleset(postId: string) {
      try {
        const rolesArray = this.storedComplianceRules?.roleIds.map(
          (roleId) => ({
            roleId,
          })
        );
        const pinia = usePinia() as Pinia<API>;
        await pinia.$axios.$put(pinia.$API.posts.complianceRuleset(postId), {
          ruleSet: [
            {
              requiredActionType:
                this.storedComplianceRules?.requiredActionType,
              memberParticipationType:
                this.storedComplianceRules?.memberParticipationType,
              gracePeriodMillis: 86400001,
              targetAudience: {
                roles: this.storedComplianceRules?.channelWide
                  ? []
                  : rolesArray,
                channelWide: this.storedComplianceRules?.channelWide,
              },
            },
          ],
        });
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in compliance ruleset:',
          error
        );
      }
    },
    complianceAcknowledgeData(payload: AcknowledgeData) {
      this.acknowledgeData = payload;
    },
    async acknowledgeReceipt(postId: string) {
      try {
        const pinia = usePinia() as Pinia<API>;
        await pinia.$axios.$post(
          pinia.$API.posts.postAcknowledgeComplianceAction(postId),
          {
            teamId: this.acknowledgeData?.teamId,
            ruleId: this.acknowledgeData?.ruleId,
          }
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in compliance acknowledge data:',
          error
        );
      }
    },
    async verifySignature(payload: {
      postId: string;
      recordId: number;
    }): Promise<void> {
      const { postId, recordId } = payload;
      try {
        const pinia = usePinia() as Pinia<API>;
        await pinia.$axios.$post(
          pinia.$API.posts.verifyComplianceRecordSignature(postId, recordId)
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error verifying signature:', error);
      }
    },
    async postUnreadMarker(postId: string) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp: string = await pinia.$axios.$post(
          pinia.$API.posts.unreadMarker(postId),
          []
        );
        this.markUnread = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in post unread marker:',
          error
        );
      }
    },
    async deleteUnreadMarker(postId: string) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp: string = await pinia.$axios.$delete(
          pinia.$API.posts.unreadMarker(postId),
          []
        );
        this.markUnread = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error deleting unread marker:',
          error
        );
      }
    },
    updateMarkedAsUnread(markedAsUnread: Record<string, Post>) {
      const postId = markedAsUnread.post?.postDetail?.postId;
      const version = markedAsUnread.post?.postDetail?.version;

      if (postId && version) {
        const key = `${postId}>${version}`;

        if (this.posts[key]?.postDetail) {
          this.posts[key].postDetail.markedAsUnread =
            !this.posts[key].postDetail.markedAsUnread;
        }
      }
    },
    updateChannelTimeline() {
      const values = Object.values(this.posts);
      const parsedChannelId =
        typeof this.currentChannelId === 'string'
          ? parseInt(this.currentChannelId)
          : this.currentChannelId;
      if (Object.values(this.posts).length === 0) {
        this.postsShown = [];
      }
      const postsByChannel = values.filter(
        (item) => item.postDetail?.channelId === parsedChannelId
      );
      const unqiuePosts = [
        ...new Map(
          postsByChannel.map((post) => [post.postDetail?.postId, post])
        ).values(),
      ];
      this.postsShown = unqiuePosts;
      this.reqActions = postsByChannel.filter(
        (item) => !item.postDetail?.metadata?.compliance?.isCompliant
      );
      this.unreadPosts = this.unreadPosts.filter(
        (unread) => unread.postDetail?.versionLabel !== 'DRAFT'
      );
    },

    removePostFromFeed(payload: string) {
      this.posts = Object.fromEntries(
        Object.entries(this.posts).filter(
          ([_, post]) => post.postDetail?.postId !== payload
        )
      );
    },

    async subsribeUserToPostNotification(payload: {
      postId: string;
      isSubscribed: boolean;
      userId: number;
    }): Promise<void> {
      const { postId, isSubscribed, userId } = payload;
      const pinia = usePinia() as Pinia<API>;
      try {
        if (isSubscribed) {
          await pinia.$axios.$post(
            pinia.$API.user.subscribeUserToPost(postId),
            {
              isSubscribed,
              userId,
            }
          );
        } else if (!isSubscribed) {
          await pinia.$axios.$delete(
            pinia.$API.user.subscribeUserToPost(postId)
          );
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in subscribing user to post notification:',
          error
        );
      }
    },
    async getServiceItemDetails(serviceItemId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp: MemberPost[] = await pinia.$axios.$get(
          pinia.$API.posts.fetchServiceItem(serviceItemId)
        );
        this.serviceItemDetail = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in getting service item details',
          error
        );
      }
    },

    async getServiceItemDocuments(serviceItemId: number) {
      const pinia = usePinia() as Pinia<API>;

      if (this.documentsByServiceId[serviceItemId]) {
        this.orderDocuments = this.documentsByServiceId[serviceItemId];
        return;
      }

      try {
        const data: ServiceItemDocumentsResponse | null =
          await pinia.$axios.$get(
            pinia.$API.posts.fetchServiceItemDocuments(serviceItemId)
          );

        const documents =
          data && Array.isArray(data.orderDocuments) ? data.orderDocuments : [];
        const count =
          data && typeof data.orderDocumentCount === 'number'
            ? data.orderDocumentCount
            : 0;

        this.orderDocuments = documents;
        this.orderDocumentCount = count;

        this.documentsByServiceId[serviceItemId] = documents;
      } catch (error) {
        handleErrorAndNotificationHandler(error);

        this.orderDocuments = [];
        this.orderDocumentCount = 0;
        this.documentsByServiceId[serviceItemId] = [];
      }
    },

    mutatePostDetail(payload: {
      key: string;
      subscribedToPostCommentActivity: boolean;
      subscribedToPostActivity: boolean;
    }) {
      const { key, subscribedToPostCommentActivity, subscribedToPostActivity } =
        payload;
      if (this.posts[key].postDetail.subscriptions) {
        this.posts[key].postDetail.subscriptions.subscribedToPostActivity =
          subscribedToPostActivity;
        this.posts[
          key
        ].postDetail.subscriptions.subscribedToPostCommentActivity =
          subscribedToPostCommentActivity;
      }
    },
  },

  getters: {
    getAllPostsGetter: (state) => {
      return state.posts;
    },
    latestPostDetailGetter: (state) => state.latestPostDetail,
    getclassificationNodesGetter: (state) => state.classificationNodes,
    getCategoryGetter: (state) => state.classCategory,
    getActiveClassificationGetter: (state) => state.activeClassification,
    getSubcategoryGetter: (state) => state.classSubcategory,
    memberPostsTodayGetter: (state) => {
      let arr;
      if (state.pageName === 'channel-channelId-timeline') {
        arr = state.postsShown;
      } else if (state.pageName.includes('classifications')) {
        arr = state.classificationsPostsShown;
      } else if (
        state.pageName === 'channel-channelId-timeline-unread-posts' ||
        state.pageName === 'channels-unread-posts'
      ) {
        arr = state.unreadPosts;
      } else if (
        state.pageName === 'channel-channelId-timeline-required-actions' ||
        state.pageName === 'channels-required-actions'
      ) {
        arr = state.reqActions;
      }

      const todayInterval = Interval.fromDateTimes(
        DateTime.now().startOf('day'),
        DateTime.now().endOf('day')
      );

      return filterPosts(arr, todayInterval);
    },
    memberPostsYesterdayGetter: (state) => {
      let arr;
      if (state.pageName === 'channel-channelId-timeline') {
        arr = state.postsShown;
      } else if (state.pageName.includes('classifications')) {
        arr = state.classificationsPostsShown;
      } else if (
        state.pageName === 'channel-channelId-timeline-unread-posts' ||
        state.pageName === 'channels-unread-posts'
      ) {
        arr = state.unreadPosts;
      } else if (
        state.pageName === 'channel-channelId-timeline-required-actions' ||
        state.pageName === 'channels-required-actions'
      ) {
        arr = state.reqActions;
      }

      const yesterdayInterval = Interval.fromDateTimes(
        DateTime.now().minus({ days: 1 }).startOf('day'),
        DateTime.now().minus({ days: 1 }).endOf('day')
      );

      return filterPosts(arr, yesterdayInterval);
    },
    memberPostsThisWeekGetter(state) {
      let arr;
      if (state.pageName === 'channel-channelId-timeline') {
        arr = state.postsShown;
      } else if (state.pageName.includes('classifications')) {
        arr = state.classificationsPostsShown;
      } else if (
        state.pageName === 'channel-channelId-timeline-unread-posts' ||
        state.pageName === 'channels-unread-posts'
      ) {
        arr = state.unreadPosts;
      } else if (
        state.pageName === 'channel-channelId-timeline-required-actions' ||
        state.pageName === 'channels-required-actions'
      ) {
        arr = state.reqActions;
      }

      const thisWeekInterval = Interval.fromDateTimes(
        DateTime.now().startOf('week'),
        DateTime.now().endOf('day')
      );

      const todayInterval = Interval.fromDateTimes(
        DateTime.now().startOf('day'),
        DateTime.now().endOf('day')
      );

      const yesterdayInterval = Interval.fromDateTimes(
        DateTime.now().minus({ days: 1 }).startOf('day'),
        DateTime.now().minus({ days: 1 }).endOf('day')
      );

      return filterPosts(arr, thisWeekInterval, [
        todayInterval,
        yesterdayInterval,
      ]);
    },
    memberPostsLastWeekGetter(state) {
      let arr;
      if (state.pageName === 'channel-channelId-timeline') {
        arr = state.postsShown;
      } else if (state.pageName.includes('classifications')) {
        arr = state.classificationsPostsShown;
      } else if (
        state.pageName === 'channel-channelId-timeline-unread-posts' ||
        state.pageName === 'channels-unread-posts'
      ) {
        arr = state.unreadPosts;
      } else if (
        state.pageName === 'channel-channelId-timeline-required-actions' ||
        state.pageName === 'channels-required-actions'
      ) {
        arr = state.reqActions;
      }

      const lastWeekInterval = Interval.fromDateTimes(
        DateTime.now().minus({ weeks: 1 }).startOf('week'),
        DateTime.now().minus({ weeks: 1 }).endOf('week')
      );

      return filterPosts(arr, lastWeekInterval);
    },
    memberPostsThisMonthGetter(state) {
      let arr;

      if (state.pageName === 'channel-channelId-timeline') {
        arr = state.postsShown;
      } else if (state.pageName.includes('classifications')) {
        arr = state.classificationsPostsShown;
      } else if (
        state.pageName === 'channel-channelId-timeline-unread-posts' ||
        state.pageName === 'channels-unread-posts'
      ) {
        arr = state.unreadPosts;
      } else if (
        state.pageName === 'channel-channelId-timeline-required-actions' ||
        state.pageName === 'channels-required-actions'
      ) {
        arr = state.reqActions;
      }

      // Full month interval
      const monthInterval = Interval.fromDateTimes(
        DateTime.now().startOf('month'),
        DateTime.now().endOf('day')
      );

      // Exclude these intervals (to avoid overlap)
      const todayInterval = Interval.fromDateTimes(
        DateTime.now().startOf('day'),
        DateTime.now().endOf('day')
      );

      const yesterdayInterval = Interval.fromDateTimes(
        DateTime.now().minus({ days: 1 }).startOf('day'),
        DateTime.now().minus({ days: 1 }).endOf('day')
      );

      const thisWeekInterval = Interval.fromDateTimes(
        DateTime.now().startOf('week'),
        DateTime.now().endOf('day')
      );

      const lastWeekInterval = Interval.fromDateTimes(
        DateTime.now().minus({ weeks: 1 }).startOf('week'),
        DateTime.now().minus({ weeks: 1 }).endOf('week')
      );

      // Use our helper with exclusions
      return filterPosts(arr, monthInterval, [
        todayInterval,
        yesterdayInterval,
        thisWeekInterval,
        lastWeekInterval,
      ]);
    },

    draftPostsGetter: (state) => {
      let arr;
      if (state.pageName === 'channel-channelId-timeline') {
        arr = state.postsShown;
      }
      const channelId =
        typeof state.currentChannelId === 'string'
          ? parseInt(state.currentChannelId)
          : state.currentChannelId;
      arr = arr.filter((post) => post.postDetail?.channelId === channelId);
      return arr.filter((item) => item.postDetail?.versionLabel === 'DRAFT');
    },
    memberPostsArchiveGetter(state): Collections[] {
      let arr;

      if (state.pageName === 'channel-channelId-timeline') {
        arr = state.postsShown;
      } else if (state.pageName.includes('classifications')) {
        arr = state.classificationsPostsShown;
      } else if (
        state.pageName === 'channel-channelId-timeline-unread-posts' ||
        state.pageName === 'channels-unread-posts'
      ) {
        arr = state.unreadPosts;
      } else if (
        state.pageName === 'channel-channelId-timeline-required-actions' ||
        state.pageName === 'channels-required-actions'
      ) {
        arr = state.reqActions;
      } else {
        return []; // Explicitly return an empty array if none of the conditions match
      }

      const thisWeekPosts = this.memberPostsThisWeekGetter;
      const lastWeekPosts = this.memberPostsLastWeekGetter;
      const excludedPosts = [...thisWeekPosts, ...lastWeekPosts].map(
        (post) => post.postDetail?.postId
      );

      const preparedThisMonthPosts = arr.filter(
        (post) => !excludedPosts.includes(post.postDetail?.postId)
      );
      arr = preparedThisMonthPosts.filter(
        (item) => item.postDetail?.versionLabel !== 'DRAFT'
      );
      const items = [...arr];

      if (!items.length) {
        return [];
      }

      const sortedItems = items.sort(
        (a, b) =>
          (a.postDetail?.lastPublishDateTimeMillis ?? 0) -
          (b.postDetail?.lastPublishDateTimeMillis ?? 0)
      );

      const oldest = sortedItems[0]?.postDetail?.lastPublishDateTimeMillis
        ? DateTime.fromMillis(
            sortedItems[0].postDetail.lastPublishDateTimeMillis
          )
        : null;

      const now = DateTime.now();
      const collection: Collections[] = [];

      for (let i = 0; i < now.diff(oldest, 'months').months; i++) {
        const interval = Interval.fromDateTimes(
          now.minus({ months: i + 1 }).startOf('month'),
          now.minus({ months: i + 1 }).endOf('month')
        );

        const itemsCollection = arr
          ?.filter(
            (item) =>
              Boolean(item.postDetail) &&
              typeof item.postDetail.lastPublishDateTimeMillis === 'number' &&
              interval.contains(
                DateTime.fromMillis(item.postDetail.lastPublishDateTimeMillis)
              )
          )
          .sort((a, b) => {
            const aTime = a.postDetail?.lastPublishDateTimeMillis ?? 0;
            const bTime = b.postDetail?.lastPublishDateTimeMillis ?? 0;
            return bTime - aTime;
          });

        const isLastMonth = i === 0;

        const titleShown = isLastMonth
          ? 'Last Month'
          : DateTime.now()
              .minus({ months: i + 1 })
              .toFormat('MMMM yyyy');

        collection.push({
          title: titleShown,
          items: itemsCollection,
        });
      }
      return collection; // Return the collection at the end of the function
    },
  },
});
